// Pure grouping for the live call-transcript overlay.
//
// The streaming STT feed (src/lib/phone/sttStream.ts) emits one segment per
// utterance chunk, each tagged with a channel (0 = you / agent mic, 1 = the
// other party). Rendering each segment with its own speaker label re-prints
// "YOU" for every chunk while a single person keeps talking — very redundant.
//
// groupTranscript() collapses CONSECUTIVE same-channel segments into one speaker
// GROUP so the UI can show the speaker label exactly once per turn, then stack
// that turn's lines beneath it (messaging-style). A new group begins only when
// the channel changes; order is preserved and each line keeps its own isFinal
// flag (so the not-yet-committed interim line can still be styled distinctly).
//
// Pure + total: never throws, tolerates a null/non-array input or holes, and
// returns [] for empty input. Kept dependency-free so it is unit-testable as a
// `npm test` regression guard (docs/REGRESSIONS.md).

export interface TranscriptSegment {
  /** 0 = you (self / agent mic), 1 = the other party. */
  channel: number
  text: string
  /** false = interim (live, not yet committed); true = final. */
  isFinal: boolean
  ts: number
}

export interface TranscriptGroupLine {
  text: string
  isFinal: boolean
  ts: number
}

export interface TranscriptGroup {
  /** The channel shared by every line in this group. */
  channel: number
  /** Convenience flag: channel 0 is the local speaker ("You"). */
  isSelf: boolean
  /** The consecutive same-channel segments, in original order. */
  lines: TranscriptGroupLine[]
}

export function groupTranscript(
  segments: ReadonlyArray<TranscriptSegment> | null | undefined,
): TranscriptGroup[] {
  if (!Array.isArray(segments) || segments.length === 0) return []
  const groups: TranscriptGroup[] = []
  for (const seg of segments) {
    if (!seg) continue
    const channel = seg.channel
    const line: TranscriptGroupLine = { text: seg.text, isFinal: seg.isFinal, ts: seg.ts }
    const last = groups.length > 0 ? groups[groups.length - 1] : undefined
    if (last && last.channel === channel) {
      last.lines.push(line)
    } else {
      groups.push({ channel, isSelf: channel === 0, lines: [line] })
    }
  }
  return groups
}
